import React, { useState, useEffect } from 'react';
import { T, FONTS } from '../../constants/theme';
import { fetchRatePlans, createRatePlan, updateRatePlan, deleteRatePlan,
         fetchRoomTypes } from '../../services/api.js';
import { OfflineBadge, useOfflineInvalidation } from '../../components/ui.jsx';

const RATE_TYPES  = ['per_night','per_person','flat','weekly','monthly'];
const MEAL_PLANS  = [
  { id:'room_only',    label:'Room Only'      },
  { id:'bb',           label:'Bed & Breakfast' },
  { id:'hb',           label:'Half Board'      },
  { id:'fb',           label:'Full Board'      },
  { id:'all_inclusive',label:'All Inclusive'   },
];
const EMPTY = {
  code:'', name:'', rateType:'per_night', mealPlan:'room_only',
  currency:'', amount:'', validFrom:'', validTo:'',
  minStay:'1', maxStay:'', isOtaRate:false, cancellationPolicy:'',
  roomTypeId:'',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:'block', fontSize:9, fontWeight:700, color:T.muted,
        letterSpacing:0.8, textTransform:'uppercase', marginBottom:4 }}>{label}</label>
      {children}
    </div>
  );
}

const inp = (v, onChange, type='text', ph='') => (
  <input type={type} value={v} onChange={e=>onChange(e.target.value)} placeholder={ph}
    style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px',
      fontSize:12, fontFamily:FONTS.body, color:T.navy, outline:'none', boxSizing:'border-box' }}
    onFocus={e=>e.target.style.borderColor=T.teal}
    onBlur={e=>e.target.style.borderColor=T.border} />
);

const sel = (v, onChange, options) => (
  <select value={v} onChange={e=>onChange(e.target.value)}
    style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px',
      fontSize:12, fontFamily:FONTS.body, color:T.navy, outline:'none' }}>
    {options}
  </select>
);

export default function RatePlans({ property }) {
  const [plans,     setPlans]     = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState('');
  const pid = property?.id;

  const offlineTick = useOfflineInvalidation('rate-plans');

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const [p, rt] = await Promise.all([fetchRatePlans(pid), fetchRoomTypes(pid)]);
      setPlans(Array.isArray(p) ? p : []);
      setRoomTypes(Array.isArray(rt) ? rt : []);
    } catch { setPlans([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid, offlineTick]); // eslint-disable-line

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.code.trim()) { showToast('Rate code is required'); return; }
    if (!form.amount)      { showToast('Amount is required');    return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        propertyId: pid,
        amount:   parseFloat(form.amount),
        minStay:  parseInt(form.minStay) || 1,
        maxStay:  form.maxStay ? parseInt(form.maxStay) : null,
        validFrom: form.validFrom || null,
        validTo:   form.validTo   || null,
        roomTypeId:form.roomTypeId || null,
        currency:  form.currency || property.currency || 'USD',
      };
      if (editId) {
        const res = await updateRatePlan(editId, payload);
        showToast(res?._offline ? '⏳ Saved offline — will sync when server is back' : '✅ Rate plan updated');
      } else {
        const res = await createRatePlan(payload);
        showToast(res?._offline ? '⏳ Saved offline — will sync when server is back' : '✅ Rate plan created');
      }
      setShowForm(false); setEditId(null); setForm(EMPTY); load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Deactivate rate plan "${code}"?`)) return;
    try { await deleteRatePlan(id); showToast(`✅ ${code} deactivated`); load(); }
    catch (e) { showToast(`❌ ${e.message}`); }
  };

  const openEdit = p => {
    setForm({
      code:      p.code || '',
      name:      p.name || '',
      rateType:  p.rateType || 'per_night',
      mealPlan:  p.mealPlan || 'room_only',
      currency:  p.currency || '',
      amount:    p.amount || '',
      validFrom: p.validFrom || '',
      validTo:   p.validTo   || '',
      minStay:   p.minStay?.toString() || '1',
      maxStay:   p.maxStay?.toString() || '',
      isOtaRate: p.isOtaRate || false,
      cancellationPolicy: p.cancellationPolicy || '',
      roomTypeId: p.roomTypeId || '',
    });
    setEditId(p.id); setShowForm(true);
  };

  return (
    <div className="fade-up">
      {toast && (
        <div style={{ position:'fixed', top:70, right:24, zIndex:999, background:T.navy,
          color:T.white, borderRadius:10, padding:'11px 18px', fontSize:13, fontWeight:600,
          boxShadow:'0 8px 24px rgba(0,0,0,0.2)' }}>{toast}</div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:FONTS.display, fontSize:28, fontWeight:700, color:T.navy }}>Rate Plans</h1>
          <p style={{ fontSize:12, color:T.muted, marginTop:2 }}>{plans.length} active rate plans</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(s => !s); }}
          style={{ background:T.navy, border:'none', borderRadius:10, padding:'10px 20px',
            fontSize:13, fontWeight:700, color:T.white, cursor:'pointer', fontFamily:FONTS.body }}>
          + New Rate Plan
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background:T.tealPale, border:`1px solid ${T.teal}30`, borderRadius:14,
          padding:22, marginBottom:20 }}>
          <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:16 }}>
            {editId ? 'Edit Rate Plan' : 'New Rate Plan'}
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:14, marginBottom:12 }}>
            <Field label="Rate Code *">{inp(form.code, v=>set('code',v.toUpperCase()), 'text', 'BAR')}</Field>
            <Field label="Name">{inp(form.name, v=>set('name',v), 'text', 'Best Available Rate')}</Field>
            <Field label="Rate Type">{sel(form.rateType, v=>set('rateType',v),
              RATE_TYPES.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>))}</Field>
            <Field label="Meal Plan">{sel(form.mealPlan, v=>set('mealPlan',v),
              MEAL_PLANS.map(m=><option key={m.id} value={m.id}>{m.label}</option>))}</Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:14, marginBottom:12 }}>
            <Field label={`Amount (${form.currency||property?.currency||'USD'}) *`}>
              {inp(form.amount, v=>set('amount',v), 'number', '79')}</Field>
            <Field label="Currency">{inp(form.currency, v=>set('currency',v.toUpperCase()), 'text', property?.currency||'USD')}</Field>
            <Field label="Valid From">{inp(form.validFrom, v=>set('validFrom',v), 'date')}</Field>
            <Field label="Valid To">{inp(form.validTo, v=>set('validTo',v), 'date')}</Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:14, marginBottom:16 }}>
            <Field label="Min Stay (nights)">{inp(form.minStay, v=>set('minStay',v), 'number', '1')}</Field>
            <Field label="Max Stay">{inp(form.maxStay, v=>set('maxStay',v), 'number', '')}</Field>
            <Field label="Room Type (optional)">{sel(form.roomTypeId, v=>set('roomTypeId',v),
              [<option key="" value="">All room types</option>,
               ...roomTypes.map(rt=><option key={rt.id} value={rt.id}>{rt.name} ({rt.code})</option>)])}</Field>
            <Field label="OTA Rate">
              <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, cursor:'pointer' }}>
                <input type="checkbox" checked={form.isOtaRate} onChange={e=>set('isOtaRate',e.target.checked)}
                  style={{ accentColor:T.teal }} />
                <span style={{ fontSize:12, color:T.slate }}>Is OTA channel rate</span>
              </label>
            </Field>
          </div>
          <Field label="Cancellation Policy (JSON or text)">
            <textarea value={form.cancellationPolicy} onChange={e=>set('cancellationPolicy',e.target.value)}
              placeholder='{"hours":24,"penaltyPct":100} or "Free cancellation until 24h before arrival"'
              rows={2} style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px',
                fontSize:11, fontFamily:FONTS.body, resize:'vertical', outline:'none', boxSizing:'border-box' }} />
          </Field>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY); }}
              style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:9, padding:'9px 18px',
                fontSize:12, cursor:'pointer', fontFamily:FONTS.body, color:T.muted }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ background:saving?T.muted:T.teal, border:'none', borderRadius:9, padding:'9px 24px',
                fontSize:12, fontWeight:700, cursor:saving?'not-allowed':'pointer',
                fontFamily:FONTS.body, color:T.white }}>
              {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Rate Plan'}
            </button>
          </div>
        </div>
      )}

      {/* Rate plan grid */}
      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
          {Array.from({length:4}).map((_,i) => (
            <div key={i} style={{ height:180, borderRadius:14, background:T.border }} className="shimmer" />
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div style={{ background:T.surface, border:`2px dashed ${T.border}`, borderRadius:14,
          padding:40, textAlign:'center' }}>
          <p style={{ fontSize:32, marginBottom:12 }}>💰</p>
          <p style={{ fontSize:14, fontWeight:600, color:T.slate, marginBottom:4 }}>No rate plans yet</p>
          <p style={{ fontSize:12, color:T.muted }}>Create your first rate plan to enable bookings</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
          {plans.map(plan => {
            const rt = roomTypes.find(t => t.id === plan.roomTypeId);
            return (
              <div key={plan.id} style={{ background:T.white, border:`1px solid ${T.border}`,
                borderRadius:14, overflow:'hidden' }}>
                <div style={{ height:3, background:`linear-gradient(90deg,${T.gold},${T.gold}44)` }} />
                <div style={{ padding:18 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <p style={{ fontSize:14, fontWeight:700, color:T.navy }}>{plan.code}</p>
                        {plan.isOtaRate && (
                          <span style={{ fontSize:9, color:T.teal, background:T.tealPale,
                            padding:'2px 6px', borderRadius:99, fontWeight:700 }}>OTA</span>
                        )}
                        <OfflineBadge item={plan} />
                      </div>
                      <p style={{ fontSize:11, color:T.muted }}>{plan.name || '—'}</p>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p style={{ fontSize:20, fontWeight:700, color:T.navy }}>
                        {plan.currency} {parseFloat(plan.amount||0).toLocaleString()}
                      </p>
                      <p style={{ fontSize:9, color:T.muted }}>
                        {plan.rateType?.replace('_',' ')}
                      </p>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                    {[
                      MEAL_PLANS.find(m=>m.id===plan.mealPlan)?.label || plan.mealPlan,
                      `Min ${plan.minStay||1} night${(plan.minStay||1)!==1?'s':''}`,
                      rt ? `${rt.name}` : 'All rooms',
                      plan.validFrom ? `${plan.validFrom} → ${plan.validTo||'∞'}` : null,
                    ].filter(Boolean).map(tag => (
                      <span key={tag} style={{ fontSize:9, color:T.slate, background:T.surface,
                        border:`1px solid ${T.border}`, padding:'3px 7px', borderRadius:99 }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  {plan.cancellationPolicy && (
                    <p style={{ fontSize:10, color:T.muted, marginBottom:12, fontStyle:'italic' }}>
                      📋 {typeof plan.cancellationPolicy === 'string'
                          ? plan.cancellationPolicy.substring(0,80)
                          : JSON.stringify(plan.cancellationPolicy).substring(0,80)}
                    </p>
                  )}
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => openEdit(plan)}
                      style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`,
                        borderRadius:8, padding:'7px 0', fontSize:11, cursor:'pointer',
                        fontFamily:FONTS.body, color:T.slate, fontWeight:600 }}>
                      ✏️ Edit
                    </button>
                    <button onClick={() => handleDelete(plan.id, plan.code)}
                      style={{ flex:1, background:T.dangerPale, border:`1px solid ${T.danger}30`,
                        borderRadius:8, padding:'7px 0', fontSize:11, cursor:'pointer',
                        fontFamily:FONTS.body, color:T.danger, fontWeight:600 }}>
                      Deactivate
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
